import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import type { BoxProps } from '../box/Box';
import { Box } from '../box/Box';
import Image from '../image/Image';
import { Text } from '../text/Text';
import { Wizard } from '../wizard/Wizard';
import type { UseWizardResult } from '../wizard/wizard.types';
import styles from './Settings.Wizard.module.scss';

type SettingsWizardSharedProps = {
  /** @link https://www.radix-ui.com/primitives/docs/utilities/slot */
  asChild?: boolean;
};

/**
 * Wizard Container
 */
const cvaWizard = cva(styles.wizardContainer);

export type SettingsWizardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cvaWizard> &
  SettingsWizardSharedProps & {};

const SettingsWizard = React.forwardRef<HTMLDivElement, SettingsWizardProps>(
  ({ asChild, children, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        {...props}
        ref={forwardedRef}
        className={cvaWizard({ className: props.className })}
      >
        {children}
      </Component>
    );
  }
);
SettingsWizard.displayName = 'Settings.Wizard';

/**
 * Wizard Sidebar
 */
export type SettingsWizardSidebarProps<T = Record<string, unknown>> =
  React.HTMLAttributes<HTMLDivElement> &
    SettingsWizardSharedProps & {
      /** Wizard instance from useWizard hook */
      wizard: UseWizardResult<T>;
      /** App logo/icon - either a URL string or React component */
      icon?: string | React.ReactNode;
      /** App name/title */
      title?: string;
      /** App description */
      description?: string;
    };

const SettingsWizardSidebar = React.forwardRef<HTMLDivElement, SettingsWizardSidebarProps<any>>(
  ({ wizard, icon, title, description, asChild, children, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'aside';

    return (
      <Component {...props} ref={forwardedRef} className={styles.wizardSidebar}>
        <Box.Column height="100%" padding="xlarge" gap="xlarge">
          {/* App Info Section */}
          {(icon || title || description) && (
            <Box.Column gap="small" marginBottom={4 * 10}>
              {icon && (
                <Box marginBottom={4 * 2}>
                  {typeof icon === 'string' ? (
                    <Image src={icon} width={48} height={48} style={{ borderRadius: 8 }} />
                  ) : (
                    icon
                  )}
                </Box>
              )}
              {title && <Text.Headline size="xsmall">{title}</Text.Headline>}
              {description && <Text color="subtle">{description}</Text>}
            </Box.Column>
          )}
          <Wizard.ProgressList wizard={wizard} />
          <Box.Spacer />
          {children}
        </Box.Column>
      </Component>
    );
  }
);
SettingsWizardSidebar.displayName = 'Settings.Wizard.Sidebar';

/**
 * Wizard Step
 */
export type SettingsWizardStepProps = React.HTMLAttributes<HTMLDivElement> &
  SettingsWizardSharedProps & {};

const SettingsWizardStep = React.forwardRef<HTMLDivElement, SettingsWizardStepProps>(
  ({ asChild, children, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component {...props} ref={forwardedRef} className={styles.wizardStep}>
        {children}
      </Component>
    );
  }
);
SettingsWizardStep.displayName = 'Settings.Wizard.Step';

/**
 * Wizard Step Header
 */
export type SettingsWizardStepHeaderProps = React.HTMLAttributes<HTMLDivElement> &
  SettingsWizardSharedProps & {
    title?: string;
    description?: string;
  };

const SettingsWizardStepHeader = React.forwardRef<HTMLDivElement, SettingsWizardStepHeaderProps>(
  ({ title, description, asChild, children, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';

    return (
      <Component {...props} ref={forwardedRef} className={styles.wizardStepHeader}>
        <Box.Row gap="medium" alignItems="center">
          <Box.Column gap="small">
            {title && <Text.Headline size="small">{title}</Text.Headline>}
            {description && <Text color="subtle">{description}</Text>}
          </Box.Column>
        </Box.Row>
        {children}
      </Component>
    );
  }
);
SettingsWizardStepHeader.displayName = 'Settings.Wizard.Step.Header';

/**
 * Wizard Step Content
 */
export type SettingsWizardStepContentProps = React.HTMLAttributes<HTMLDivElement> &
  SettingsWizardSharedProps & {};

const SettingsWizardStepContent = React.forwardRef<HTMLDivElement, SettingsWizardStepContentProps>(
  ({ asChild, children, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component {...props} ref={forwardedRef} className={styles.wizardStepContent}>
        {children}
      </Component>
    );
  }
);
SettingsWizardStepContent.displayName = 'Settings.Wizard.Step.Content';

/**
 * Wizard Step Footer
 */
export type SettingsWizardStepFooterProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<BoxProps, 'as'> &
  SettingsWizardSharedProps & {};

const SettingsWizardStepFooter = React.forwardRef<HTMLDivElement, SettingsWizardStepFooterProps>(
  ({ asChild, ...props }, forwardedRef) => {
    const Component = asChild ? Slot : Box.Row;
    return (
      <Component
        gap="large"
        justifyContent="flex-end"
        {...props}
        ref={forwardedRef}
        className={styles.wizardStepFooter}
      />
    );
  }
);
SettingsWizardStepFooter.displayName = 'Settings.Wizard.Step.Footer';

// Just a way to lay out wizard step and error message as column
const SettingsWizardMain = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <div {...props} ref={forwardedRef} className={styles.wizardMain}>
        {children}
      </div>
    );
  }
);
SettingsWizardMain.displayName = 'Settings.Wizard.Main';

export const SettingsWizardComponents = {
  Root: SettingsWizard,
  Sidebar: SettingsWizardSidebar,
  Main: SettingsWizardMain,
  Step: Object.assign(SettingsWizardStep, {
    Header: SettingsWizardStepHeader,
    Content: SettingsWizardStepContent,
    Footer: SettingsWizardStepFooter,
  }),
};
